/**
 * components/EmptyState.jsx — Diplomexa Institutional Luxury empty-state.
 *
 * Used by:
 *  - components/kit/DataTable.jsx
 *  - Many admin/analytics surfaces
 *
 * Visual language:
 *  - Sharp-corner card with parchment noise + gold-pulse icon halo
 *  - Cormorant Garamond headline
 *  - Eyebrow ("Nothing here yet") above the headline
 *  - Optional primary + secondary CTAs
 *
 * Props (BACKWARDS COMPATIBLE):
 *  - icon:        lucide-react component
 *  - eyebrow:     overrides the default "Nothing here yet"
 *  - title:       Cormorant headline (required)
 *  - description: smaller slate sub-line
 *  - primary:     { label, onClick, testId }      → gold CTA
 *  - secondary:   { label, onClick, testId }      → ghost CTA
 *  - testId:      data-testid for the container
 *  - tone:        "light" (default) | "dark" | "dossier"
 *  - className:   extra Tailwind classes (passed to the wrapper)
 */
import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";


export default function EmptyState({
  icon: Icon,
  eyebrow,
  title,
  description,
  primary,
  secondary,
  testId,
  tone = "light",
  className = "",
}) {
  const isDark = tone === "dark" || tone === "dossier";
  const IconBg = isDark
    ? "bg-white/5 border border-brand-gold/40 text-brand-gold"
    : "bg-brand-navy-dark text-brand-gold shadow-gold-soft";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 220, damping: 22 }}
      data-testid={testId || "empty-state"}
      className={`relative overflow-hidden rounded-sm border p-10 sm:p-14 text-center ${
        isDark
          ? "bg-brand-navy-dark border-brand-gold/30 text-brand-champagne"
          : "bg-white border-slate-200"
      } ${className}`}
    >
      {isDark ? (
        <>
          <div aria-hidden className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
          <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
          <div aria-hidden className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand-gold/12 blur-3xl" />
        </>
      ) : null}

      <div className="relative inline-flex items-center justify-center mb-5">
        <div aria-hidden className={`absolute inset-0 rounded-full ${isDark ? "bg-brand-gold/20" : "bg-brand-gold/15"} blur-2xl animate-gold-pulse`} />
        <div className={`relative h-14 w-14 rounded-sm flex items-center justify-center ${IconBg}`}>
          {Icon ? <Icon className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
        </div>
      </div>

      <p className={`eyebrow ${isDark ? "text-brand-gold" : "text-brand-gold-dark"}`}>
        {eyebrow || "Nothing here yet"}
      </p>
      <h3 className={`relative mt-3 font-display text-2xl sm:text-3xl leading-tight ${
        isDark ? "text-brand-champagne" : "text-brand-navy-dark"
      }`}>
        {title}
      </h3>
      {description ? (
        <p className={`relative mt-3 max-w-md mx-auto text-sm leading-relaxed ${
          isDark ? "text-brand-champagne/65" : "text-slate-500"
        }`}>
          {description}
        </p>
      ) : null}

      {(primary || secondary) ? (
        <div className="relative mt-7 flex items-center justify-center gap-3 flex-wrap">
          {primary ? (
            <button
              onClick={primary.onClick}
              data-testid={primary.testId || "empty-primary-cta"}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-brand-gold text-brand-navy-dark text-xs uppercase tracking-[0.2em] font-bold shadow-gold-soft hover:shadow-gold-strong hover:bg-brand-gold-light transition-all duration-300"
            >
              {primary.label}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          ) : null}
          {secondary ? (
            <button
              onClick={secondary.onClick}
              data-testid={secondary.testId || "empty-secondary-cta"}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold transition-colors border ${
                isDark
                  ? "border-brand-gold/30 text-brand-champagne hover:bg-white/5"
                  : "border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              {secondary.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </motion.div>
  );
}
